import { Link } from 'react-router-dom'
import type { ResourcesDto } from '@/api/hooks'
import { useResourceTick } from '@/hooks/useResourceTick'
import { GoldIcon, WoodIcon, RumIcon, PennantIcon } from '@/components/game/GameIcons'

/**
 * Compact, single-line resource readout shown in the global header so the
 * player always knows their treasury — no need to bounce back to the Island
 * page from the Map or the Fleet. Reuses the same client-side ticker as the
 * full ResourceBar.
 */
/**
 * Compacte les grands trésors pour que la pilule du header garde une largeur
 * bornée (à 6 chiffres × 3 ressources elle finissait par chevaucher la nav xl) :
 * ≥ 100 000 → « 683 k », ≥ 1 000 000 → « 1,2 M ». L'exact reste dans le title.
 */
export function fmtHeaderAmount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M`
  if (n >= 100_000) return `${Math.round(n / 1_000).toLocaleString('fr-FR')} k`
  return n.toLocaleString('fr-FR')
}

/**
 * Sous branle-bas (fiche #27), la pilule prend un anneau ambre et un fanion pour
 * l'ÉTAT, plus une mèche linéaire sur son bord bas pour le TEMPS : elle se
 * consume de gauche à droite et atteint zéro pile au stand-down automatique.
 * Direction « mèche qui brûle », retenue parce qu'elle n'ajoute aucune largeur au
 * header, déjà juste en xl (cf. {@see fmtHeaderAmount}).
 *
 * La mèche est rapportée à l'autonomie mesurée AU MOMENT où le branle-bas a été
 * sonné, pas à une fenêtre arbitraire : rapportée à un forfait de 48 h, elle
 * affichait la moitié dès la première minute pour qui disposait de 24 h de
 * réserves, ce qui n'avait aucun rapport avec ce que le joueur lisait à l'écran.
 *
 * RÈGLE DE COULEUR, et elle est structurante : on ne colore JAMAIS la valeur
 * d'une ressource à cause de la mobilisation. Le rouge sur un chiffre veut déjà
 * dire « entrepôt plein » ({@see ResourceChip}), et réutiliser ce canal rendrait
 * les deux états indiscernables. La mobilisation parle donc par la bordure et le
 * fanion, jamais par les chiffres. Deux canaux, deux sens.
 *
 * Aucune animation permanente : la mobilisation dure au minimum 12 h et cette
 * pilule est dans le header de toutes les pages. Seule la teinte de la jauge
 * change quand la fin approche.
 */
const AUTONOMY_WARNING_SECONDS = 6 * 3600

export function ResourceBanner({ resources }: { resources: ResourcesDto }) {
  const mobilized = !!resources.mobilized
  const urgent =
    mobilized && resources.autonomySeconds != null && resources.autonomySeconds <= AUTONOMY_WARNING_SECONDS
  const gauge = Math.max(0, Math.min(1, resources.autonomyGauge ?? 1))

  const pill = (
    <Link
      to="/island"
      className={`flex items-center gap-3 text-sm rounded-full px-4 py-1.5 border transition-colors ${
        mobilized
          ? urgent
            ? 'border-red-400/70 bg-ocean-900/80'
            : 'border-amber-300/70 bg-ocean-900/80'
          : 'border-sand-500/25 bg-ocean-900/60 hover:border-sand-300/40'
      }`}
      title={mobilized ? 'Branle-bas de combat : voir mon île' : 'Voir mon île'}
    >
      {mobilized && (
        <>
          <PennantIcon
            className={`w-4 h-4 shrink-0 ${urgent ? 'text-red-400' : 'text-amber-300'}`}
            aria-label="Branle-bas de combat"
          />
          <span className="w-px h-4 bg-sand-500/25" />
        </>
      )}
      <ResourceChip Icon={GoldIcon} label="Or" base={resources.gold} perHour={resources.goldPerHour} capacity={resources.capacity} />
      <span className="w-px h-4 bg-sand-500/25" />
      <ResourceChip Icon={WoodIcon} label="Bois" base={resources.wood} perHour={resources.woodPerHour} capacity={resources.capacity} />
      <span className="w-px h-4 bg-sand-500/25" />
      <ResourceChip Icon={RumIcon} label="Rhum" base={resources.rum} perHour={resources.rumPerHour} capacity={resources.capacity} />
    </Link>
  )

  if (!mobilized) return pill

  // La mèche est une jauge LINÉAIRE qui se consume de gauche à droite, posée sur
  // le bord bas de la pilule. Un dégradé conique (première version) était le
  // mauvais outil : sur une pilule large et basse, sa frontière angulaire se lit
  // comme « la moitié gauche est surlignée », pas comme une mèche qui brûle.
  const burnt = `${(gauge * 100).toFixed(1)}%`

  return (
    <span
      className="relative inline-block"
      data-testid="mobilization-fuse"
      // Exposés en attributs pour le test autant que pour l'inspection : jsdom
      // écarte silencieusement un dégradé, donc l'attribut style ne peut pas
      // servir d'assertion.
      data-gauge={burnt}
      data-urgent={urgent ? 'true' : 'false'}
    >
      {pill}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-3 bottom-0 h-[2px] rounded-full ${
          urgent ? 'bg-red-400' : 'bg-amber-300'
        }`}
        style={{ width: `calc((100% - 1.5rem) * ${gauge})` }}
      />
    </span>
  )
}

function ResourceChip({
  Icon,
  label,
  base,
  perHour,
  capacity,
}: {
  Icon: (p: { className?: string }) => React.ReactNode
  label: string
  base: number
  perHour: number
  capacity: number
}) {
  const value = useResourceTick(base, perHour, capacity)
  const atCapacity = capacity > 0 && value >= capacity

  return (
    <span
      className="flex items-baseline gap-1.5 whitespace-nowrap"
      aria-label={label}
      title={`${label} : ${value.toLocaleString('fr-FR')} / ${capacity.toLocaleString('fr-FR')}`}
    >
      <Icon className={`w-4 h-4 self-center ${atCapacity ? 'text-red-400' : 'text-sand-300'}`} />
      <span className={`font-display tabular-nums ${atCapacity ? 'text-red-400' : 'text-sand-300'}`}>
        {fmtHeaderAmount(value)}
      </span>
      {/* The warehouse cap is supplementary — shown only on wide desktops (3xl+);
          on smaller widths the header keeps just the value so the nav + treasury
          fit on one row. The at-capacity red styling above still warns regardless.
          3xl et non 2xl : les trois plafonds pèsent 108 px, le plus gros poste des
          381 px que 2xl révélait alors que le conteneur n'y gagnait que 320 px. La
          nav débordait par-dessus cette pilule ; c'est réglé en la servant au
          palier qui a la place (voir tailwind.config.js). */}
      <span className="hidden 3xl:inline text-[11px] text-ocean-300">/ {fmtHeaderAmount(capacity)}</span>
    </span>
  )
}
